import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {defineMessages, useIntl} from 'react-intl';
import {view} from '@sail/ui/deprecated';
import {useWidgetControls} from '@stripe-internal/conversations-ui';
import {Conversation} from '@stripe-internal/conversations-client';
import {useClientAnalytics} from '../../Providers/ClientProvider/useClientAnalytics';
import {SurveyForm} from '../../components/Forms/CSAT/SurveyForm';
import {RatingValue} from '../../components/Forms/CSAT/types';
import {ScreenName, useScreen} from '../../Providers/ScreenProvider';
import {useApi} from '../../Providers/ApiProvider/ApiProvider';

export type Props = {
  csatSurveyToken: string;
  conversation: Conversation;
};

const messages = defineMessages({
  genericError: {
    id: 'support_conversations_client.csat.genericError',
    description:
      'Generic error message for when user is not able to submit a CSAT rating',
    defaultMessage: 'An error occurred. Please try again later.',
  },
});

export function CSATScreen({csatSurveyToken, conversation}: Props) {
  const intl = useIntl();
  const analytics = useClientAnalytics();
  const {close} = useWidgetControls();
  const {screen} = useScreen();
  const {submitCSATRating} = useApi();
  const [errorMessage, setErrorMessage] = useState<string>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const sentImpression = useRef<boolean>(false);

  const analyticsParams = useMemo(
    () => ({
      universalHelpConversationId: conversation.id,
      audience: conversation.context.audience,
      userId: conversation.user.id,
    }),
    [conversation],
  );

  useEffect(() => {
    if (!sentImpression.current) {
      analytics.action(
        'support_conversations_csat_survey_impression',
        analyticsParams,
      );
      sentImpression.current = true;
    }
  }, [analytics, analyticsParams]);

  const handleClose = useCallback(() => {
    if (conversation.context.csatToken) {
      conversation.setContextValue('csatToken', undefined);
    }

    close(true);

    conversation.disconnect();
    conversation.clearStorage();
  }, [conversation, close]);

  useEffect(() => {
    const sub = conversation.contextObservable().subscribe(() => {
      if (screen.name === ScreenName.CSAT && !conversation.context.csatToken) {
        handleClose();
      }
    });

    return () => sub.unsubscribe();
  }, [conversation, handleClose, screen]);

  const handleSubmit = useCallback(
    async ({rating, comment}: {rating: RatingValue; comment: string}) => {
      setIsLoading(true);
      analytics.action('support_conversations_csat_survey_submit', {
        ...analyticsParams,
        has_additional_feedback: !!comment,
        rating,
      });
      try {
        await submitCSATRating({csatSurveyToken, rating, comment});
        analytics.action(
          'support_conversations_csat_survey_submit_success',
          analyticsParams,
        );

        handleClose();
      } catch (e) {
        setErrorMessage(intl.formatMessage(messages.genericError));
        analytics.action(
          'support_conversations_csat_survey_submit_error',
          analyticsParams,
        );
      } finally {
        setIsLoading(false);
      }
    },
    [
      intl,
      analytics,
      handleClose,
      csatSurveyToken,
      submitCSATRating,
      analyticsParams,
    ],
  );

  return (
    <view.div
      css={{
        padding: 'medium',
        maxHeight: 'fill',
      }}
    >
      <SurveyForm
        error={errorMessage}
        loading={isLoading}
        onSubmit={handleSubmit}
        onDismiss={handleClose}
        analyticsParams={analyticsParams}
      />
    </view.div>
  );
}
